import {
  computed,
  nextTick,
  onActivated,
  onBeforeUnmount,
  onMounted,
  ref
} from 'vue';
import { onBeforeRouteLeave, useRouter } from 'vue-router';
import axios from 'axios';
import { API_ENDPOINTS } from '../common/js/apiConfig';
import {
  getLocationErrorMessage,
  getLocationRetryActionText,
  requestCurrentLocation
} from '../common/js/location';
import {
  clearStoreListStateRestoreFlag,
  consumeStoreListStateRestoreFlag,
  getStoreKey,
  loadFrequentStores,
  loadRecentStoreKeywords,
  loadStoreListState,
  markStoreListStateForRestore,
  parseDirectStoreCodeText,
  saveFrequentStore,
  saveRecentStoreKeyword,
  saveSelectedStore,
  saveStoreListState
} from '../common/js/store';

const DEFAULT_LOCATION_ERROR = '请确认手机定位及授权是否开启以获取附近门店';
const RESULT_MODES = new Set(['nearby', 'keyword', 'store', 'poi']);
const BROWSE_RESULT_MODES = new Set(['nearby', 'poi']);
const NEARBY_RADIUS_KILOMETERS = 10;

function createInitialLocation () {
  return {
    acronym: 'bj',
    firstChar: 'B',
    id: 1,
    name: '定位失败',
    pinyin: 'beijing',
    rank: 'S',
    latitude: '',
    longitude: ''
  };
}

function isCanceledRequest (error) {
  return !!(
    error &&
    (error.code === 'ERR_CANCELED' || error.name === 'CanceledError' || axios.isCancel?.(error))
  );
}

function normalizePlaces (response) {
  const values = response && response.data && response.data.data && response.data.data.places;
  if (!Array.isArray(values)) return [];
  return values.filter(place => (
    place &&
    Number.isFinite(Number(place.longitude)) &&
    Number.isFinite(Number(place.latitude))
  )).map(place => Object.assign({}, place, {
    longitude: Number(place.longitude),
    latitude: Number(place.latitude),
    coordinateSystem: 'gcj02'
  }));
}

function withoutCoordinates (value) {
  if (!value || typeof value !== 'object') return value;
  const output = Object.assign({}, value, {
    latitude: '',
    longitude: ''
  });
  delete output.accuracyMeters;
  delete output.timestampMs;
  return output;
}

export function useStoreQuery () {
  const router = useRouter();
  const vuecity = ref(null);
  const searchStoreRef = ref(null);
  const suggest11 = ref(null);
  const location = ref(createInitialLocation());
  const locatedCity = ref(createInitialLocation());
  const searchKey = ref('请选择');
  const stores1 = ref([]);
  const places = ref([]);
  const frequentStores = ref(loadFrequentStores());
  const recentStoreKeywords = ref(loadRecentStoreKeywords());
  const clearSearch = ref(false);
  const keyWord = ref('');
  const searchMode = ref('keyword');
  const resultMode = ref('nearby');
  const selectedPlace = ref(null);
  const txtLength = ref(0);
  const locateError = ref(false);
  const locating = ref(false);
  const retryLocationOnReturn = ref(false);
  const locationErrorMessage = ref(DEFAULT_LOCATION_ERROR);
  const isShowStoreCity = ref(false);
  const storeLoading = ref(false);
  const storeError = ref('');
  const placeLoading = ref(false);
  const placeError = ref('');
  const nearbyLoaded = ref(false);
  const resolvingStoreCode = ref(false);
  const scrollDistance = ref(0);
  const pendingScrollDistance = ref(0);
  const pendingSearchText = ref(null);
  const pendingRecentKeywordAfterCity = ref('');
  const lastSuccessfulSearchKeyword = ref('');
  let lastReturnRetryAt = 0;
  let activeStoreRequest = null;
  let activePlaceRequest = null;
  let activeStoreCodeRequest = null;
  let storeCodeRequestSeq = 0;
  let disposed = false;

  // 门店编码无需城市，因此输入框始终可用；普通关键词在查询前单独校验城市。
  const disInput1 = computed(() => false);
  const visibleStores = computed(() => stores1.value);
  const isKeywordResults = computed(() => (
    resultMode.value === 'keyword' &&
    keyWord.value.trim().length > 0
  ));
  const showBrowseSection = computed(() => (
    !resolvingStoreCode.value &&
    !isKeywordResults.value &&
    BROWSE_RESULT_MODES.has(resultMode.value)
  ));
  const browseStatus = computed(() => {
    if (!showBrowseSection.value) return 'idle';
    if (locateError.value) return 'location-error';
    if (locating.value || storeLoading.value) return 'loading';
    if (storeError.value) return 'request-error';
    if (stores1.value.length > 0) return 'success';
    if (!nearbyLoaded.value) return 'loading';
    return 'empty';
  });
  const browseStatusIsError = computed(() => (
    browseStatus.value === 'location-error' || browseStatus.value === 'request-error'
  ));
  const browseSectionTitle = computed(() => {
    if (!showBrowseSection.value || resultMode.value === 'poi') return '';
    return browseStatus.value === 'success'
      ? `附近餐厅（${stores1.value.length}家）`
      : '附近餐厅';
  });
  const nearbyRadiusKilometers = computed(() => NEARBY_RADIUS_KILOMETERS);
  const browseStatusTitle = computed(() => {
    if (browseStatus.value === 'location-error') return '无法获取当前位置';
    if (browseStatus.value === 'loading') return '正在获取附近餐厅';
    if (browseStatus.value === 'request-error') return '附近餐厅加载失败，请稍后重试';
    if (browseStatus.value !== 'empty') return '';
    return resultMode.value === 'poi'
      ? `该地点附近${nearbyRadiusKilometers.value}km内暂未找到餐厅`
      : `当前位置附近${nearbyRadiusKilometers.value}km内暂未找到餐厅`;
  });
  const browseStatusMessage = computed(() => {
    if (browseStatus.value === 'location-error') {
      return locationErrorMessage.value || '请开启手机系统定位服务，并允许浏览器访问位置。';
    }
    if (browseStatus.value === 'request-error') {
      return '可以重新加载，或切换城市后搜索门店。';
    }
    if (browseStatus.value === 'loading') {
      if (locating.value) return '正在确认您的位置并查找餐厅，请稍候。';
      return resultMode.value === 'poi'
        ? '正在加载该地点附近餐厅，请稍候。'
        : '正在加载当前位置附近餐厅，请稍候。';
    }
    if (browseStatus.value === 'empty') {
      return '可以切换城市，或搜索门店名称、编码及附近地点。';
    }
    return '';
  });
  const browsePrimaryActionText = computed(() => {
    if (browseStatus.value === 'location-error') return getLocationRetryActionText();
    if (browseStatus.value === 'request-error') return '重新加载';
    if (browseStatus.value === 'empty') return '搜索地点或门店';
    return '';
  });
  const browseSecondaryActionText = computed(() => (
    ['location-error', 'request-error', 'empty'].includes(browseStatus.value)
      ? '切换城市'
      : ''
  ));
  const nearbyTitle = computed(() => (
    resultMode.value === 'poi' && selectedPlace.value
      ? `${selectedPlace.value.name}附近餐厅`
      : ''
  ));
  const showNearbyLimit = computed(() => (
    (resultMode.value === 'nearby' || resultMode.value === 'poi') &&
    nearbyLoaded.value &&
    !storeLoading.value &&
    !storeError.value &&
    stores1.value.length > 0
  ));
  const recentQuickStores = computed(() => frequentStores.value.slice(0, 5));
  const showRecentQuickStores = computed(() => (
    !resolvingStoreCode.value &&
    resultMode.value !== 'poi' &&
    txtLength.value === 0 &&
    frequentStores.value.length > 0
  ));
  const showRecentKeywordBar = computed(() => (
    !resolvingStoreCode.value &&
    txtLength.value === 0 &&
    recentStoreKeywords.value.length > 0
  ));
  const showEmpty = computed(() => {
    return !showBrowseSection.value && !isKeywordResults.value &&
      stores1.value.length === 0 && !showRecentQuickStores.value;
  });
  const emptyText = computed(() => {
    if (resolvingStoreCode.value) return '正在识别门店...';
    if (isKeywordResults.value) return '未找到相关餐厅或位置';
    if (storeLoading.value || locating.value) return '正在定位并加载附近餐厅...';
    return storeError.value || '暂无餐厅';
  });
  // BetterScroll 依赖 data 引用变化刷新高度，因此同时纳入两类结果和界面状态。
  const scrollData = computed(() => [
    ...(showRecentQuickStores.value ? recentQuickStores.value : []),
    ...visibleStores.value,
    ...(isKeywordResults.value ? places.value : []),
    storeLoading.value,
    placeLoading.value,
    storeError.value,
    placeError.value,
    nearbyLoaded.value,
    resolvingStoreCode.value,
    showRecentQuickStores.value,
    resultMode.value,
    nearbyTitle.value,
    browseStatus.value,
    browseStatusTitle.value
  ]);

  onActivated(() => {
    clearStoreListStateRestoreFlag();
    frequentStores.value = loadFrequentStores();
    recentStoreKeywords.value = loadRecentStoreKeywords();
    syncSearchInput();
    restoreScrollPosition();
  });

  onMounted(() => {
    window.addEventListener('focus', retryLocationAfterReturn);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    syncSearchInput();
    restoreScrollPosition();
  });

  onBeforeUnmount(() => {
    disposed = true;
    window.removeEventListener('focus', retryLocationAfterReturn);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    cancelStoreRequest();
    cancelPlaceRequest();
    cancelStoreCodeRequest();
  });

  onBeforeRouteLeave(to => {
    if (to && to.name === 'detail') {
      saveCurrentListState();
      markStoreListStateForRestore();
    }
    return true;
  });

  if (!restoreListState()) {
    getLocation();
  }

  function restoreListState () {
    if (!consumeStoreListStateRestoreFlag()) return false;
    const state = loadStoreListState();
    return applyListState(state);
  }

  function applyListState (state) {
    if (!state || !Array.isArray(state.stores1)) return false;

    location.value = Object.assign({}, location.value, state.location || {});
    locatedCity.value = Object.assign(
      {},
      locatedCity.value,
      state.locatedCity && state.locatedCity.isLocatedCity === true ? state.locatedCity : {}
    );
    searchKey.value = state.searchKey || location.value.name || '请选择';
    stores1.value = state.stores1;
    places.value = Array.isArray(state.places) ? state.places : [];
    frequentStores.value = Array.isArray(state.frequentStores) ? state.frequentStores : frequentStores.value;
    keyWord.value = state.keyWord || '';
    searchMode.value = state.searchMode || 'keyword';
    resultMode.value = RESULT_MODES.has(state.resultMode)
      ? state.resultMode
      : (keyWord.value ? searchMode.value : 'nearby');
    selectedPlace.value = state.selectedPlace && typeof state.selectedPlace === 'object'
      ? state.selectedPlace
      : null;
    nearbyLoaded.value = state.nearbyLoaded === true;
    txtLength.value = typeof state.txtLength === 'number' ? state.txtLength : keyWord.value.length;
    locateError.value = state.locateError === true;
    retryLocationOnReturn.value = state.retryLocationOnReturn === true;
    locationErrorMessage.value = state.locationErrorMessage || DEFAULT_LOCATION_ERROR;
    // 旧版本的“常去餐厅”页签会强制显示城市；页签移除后按结果模式恢复。
    isShowStoreCity.value = resultMode.value === 'store';
    scrollDistance.value = typeof state.scrollDistance === 'number' ? state.scrollDistance : 0;
    pendingScrollDistance.value = scrollDistance.value;
    pendingSearchText.value = keyWord.value;
    return true;
  }

  function buildCurrentListState () {
    return {
      location: withoutCoordinates(location.value),
      locatedCity: withoutCoordinates(locatedCity.value),
      searchKey: searchKey.value,
      stores1: stores1.value,
      places: places.value,
      frequentStores: frequentStores.value,
      keyWord: keyWord.value,
      searchMode: searchMode.value,
      resultMode: resultMode.value,
      selectedPlace: selectedPlace.value,
      nearbyLoaded: nearbyLoaded.value,
      txtLength: txtLength.value,
      locateError: locateError.value,
      retryLocationOnReturn: retryLocationOnReturn.value,
      locationErrorMessage: locationErrorMessage.value,
      isShowStoreCity: isShowStoreCity.value,
      scrollDistance: scrollDistance.value
    };
  }

  function saveCurrentListState () {
    saveStoreListState(buildCurrentListState());
  }

  function isRecentSearchKeyword (keyword) {
    const value = String(keyword || '').trim();
    return !!value &&
      !value.startsWith('#') &&
      !value.startsWith('＃') &&
      !parseDirectStoreCodeText(value);
  }

  function markSuccessfulSearch (keyword) {
    const value = String(keyword || '').trim();
    lastSuccessfulSearchKeyword.value = isRecentSearchKeyword(value) ? value : '';
  }

  function clearSuccessfulSearch () {
    lastSuccessfulSearchKeyword.value = '';
  }

  function commitSuccessfulSearchKeyword () {
    const keyword = lastSuccessfulSearchKeyword.value;
    clearSuccessfulSearch();
    if (!keyword) return;
    recentStoreKeywords.value = saveRecentStoreKeyword(keyword);
  }

  function syncSearchInput () {
    nextTick(() => {
      if (pendingSearchText.value === null || !searchStoreRef.value) return;
      searchStoreRef.value.setSearchText(pendingSearchText.value);
      pendingSearchText.value = null;
    });
  }

  function setSearchInputText (text) {
    nextTick(() => {
      if (searchStoreRef.value) {
        searchStoreRef.value.setSearchText(text || '');
      }
    });
  }

  function applyRecentKeyword (keyword) {
    const value = String(keyword || '').trim();
    if (!value) return Promise.resolve();

    setSearchInputText(value);
    if (!hasSearchCity()) {
      pendingRecentKeywordAfterCity.value = value;
      keyWord.value = value;
      txtLength.value = value.length;
      searchMode.value = 'keyword';
      resultMode.value = 'keyword';
      locateError.value = false;
      locationErrorMessage.value = '';
      stores1.value = [];
      clearPlaceResults();
      showCity();
      return Promise.resolve();
    }

    return searchText(value, {trigger: 'submit'});
  }

  function restoreScrollPosition () {
    nextTick(() => {
      if (!suggest11.value || !pendingScrollDistance.value) return;
      suggest11.value.scrollTo(0, -pendingScrollDistance.value, 0);
      pendingScrollDistance.value = 0;
    });
  }

  function setScrollDistance (distance) {
    scrollDistance.value = distance || 0;
  }

  function handleScrollClick () {
    nextTick(() => {
      const input = searchStoreRef.value && searchStoreRef.value.getInputRef();
      if (input) input.blur();
    });
  }

  function focusStoreSearch () {
    nextTick(() => {
      const input = searchStoreRef.value && searchStoreRef.value.getInputRef();
      if (input) input.focus();
    });
  }

  function handleBrowsePrimaryAction () {
    if (locating.value || storeLoading.value) return Promise.resolve();
    if (browseStatus.value === 'location-error') return retryCurrentLocation();
    if (browseStatus.value === 'request-error') return refreshStores();
    if (browseStatus.value === 'empty') focusStoreSearch();
    return Promise.resolve();
  }

  function showToast () {
    if (disInput1.value) alert('请先选择城市，或允许定位后再搜索门店！');
  }

  function resetPlaceSelection () {
    selectedPlace.value = null;
    clearPlaceResults();
  }

  function getCity (city) {
    if (!city || !city.name) return;
    cancelStoreCodeRequest();
    applyLocatedCity(city);
    applyLocation(city);
    resetPlaceSelection();
    resultMode.value = txtLength.value > 0
      ? (searchMode.value === 'store' ? 'store' : 'keyword')
      : 'nearby';
    scrollDistance.value = 0;
    const pendingKeyword = pendingRecentKeywordAfterCity.value;
    pendingRecentKeywordAfterCity.value = '';
    return pendingKeyword
      ? searchText(pendingKeyword, {trigger: 'submit'})
      : refreshStores();
  }

  function showCity () {
    if (vuecity.value) vuecity.value.show();
  }

  function handleCityLocationError (message) {
    locationErrorMessage.value = message || DEFAULT_LOCATION_ERROR;
  }

  function retryCurrentLocation () {
    retryLocationOnReturn.value = true;
    return getLocation();
  }

  function handleVisibilityChange () {
    if (!document.hidden) retryLocationAfterReturn();
  }

  function retryLocationAfterReturn () {
    if (!retryLocationOnReturn.value || !locateError.value || locating.value) return;
    const now = Date.now();
    if (now - lastReturnRetryAt < 1500) return;
    lastReturnRetryAt = now;
    getLocation();
  }

  async function getLocation (options = {}) {
    if (locating.value) return;
    locating.value = true;
    locateError.value = false;
    locationErrorMessage.value = '';
    try {
      const currentLocation = await requestCurrentLocation();
      if (disposed) return;
      const nextLocatedCity = Object.assign({}, currentLocation, {isLocatedCity: true});
      applyLocatedCity(nextLocatedCity);
      applyLocation(nextLocatedCity);
      resetPlaceSelection();
      resultMode.value = txtLength.value > 0
        ? (searchMode.value === 'store' ? 'store' : 'keyword')
        : 'nearby';
      const pendingKeyword = pendingRecentKeywordAfterCity.value;
      pendingRecentKeywordAfterCity.value = '';
      if (pendingKeyword) {
        await searchText(pendingKeyword, {trigger: 'submit'});
      } else {
        await refreshStores();
      }
    } catch (error) {
      if (disposed) return;
      locateError.value = true;
      retryLocationOnReturn.value = true;
      locationErrorMessage.value = getLocationErrorMessage(error);
      stores1.value = [];
      nearbyLoaded.value = false;
      scrollDistance.value = 0;
      saveCurrentListState();
      if (options.showFailureAlert === true) {
        alert(`${locationErrorMessage.value}\n\n也可以手动选择城市后搜索门店。`);
      }
    } finally {
      if (!disposed) locating.value = false;
    }
  }

  function applyLocation (nextLocation) {
    location.value = Object.assign({}, nextLocation);
    searchKey.value = nextLocation.name;
    locateError.value = false;
    retryLocationOnReturn.value = false;
    locationErrorMessage.value = '';
  }

  function applyLocatedCity (nextLocation) {
    if (nextLocation && nextLocation.isLocatedCity === true) {
      locatedCity.value = Object.assign({}, locatedCity.value, nextLocation);
    }
  }

  function hasCoordinates (value) {
    return !!(
      value &&
      value.latitude !== '' &&
      value.longitude !== '' &&
      Number.isFinite(Number(value.latitude)) &&
      Number.isFinite(Number(value.longitude))
    );
  }

  function refreshStores () {
    if (txtLength.value > 0) {
      if (searchMode.value === 'store') {
        resultMode.value = 'store';
        return keyWord.value ? searchByStoreCodeAndName() : Promise.resolve();
      }
      resultMode.value = 'keyword';
      return searchKeywordResults();
    }
    if (resultMode.value === 'poi' && hasCoordinates(selectedPlace.value)) {
      return searchByLbs(selectedPlace.value, 'storePoiScene');
    }
    resultMode.value = 'nearby';
    return hasCoordinates(location.value)
      ? searchByLbs(location.value, 'storeScene')
      : Promise.resolve();
  }

  function scrollListToTop () {
    scrollDistance.value = 0;
    nextTick(() => {
      if (suggest11.value) suggest11.value.scrollTo(0, 0, 200);
    });
  }

  function setStoresFromResponse (response, showStoreCity, options = {}) {
    const body = response && response.data;
    if (!body || body.code !== 0) {
      throw new Error((body && body.message) || '门店加载失败，请稍后重试');
    }
    const data = body.data;
    if (!data || !Array.isArray(data.stores)) {
      throw new Error('门店数据格式异常，请稍后重试');
    }
    stores1.value = data.stores;
    isShowStoreCity.value = showStoreCity;
    nearbyLoaded.value = options.nearby === true;
    scrollListToTop();
    saveCurrentListState();
  }

  function cancelStoreRequest () {
    if (activeStoreRequest) {
      activeStoreRequest.abort();
      activeStoreRequest = null;
    }
  }

  function cancelStoreCodeRequest () {
    storeCodeRequestSeq += 1;
    if (activeStoreCodeRequest) {
      activeStoreCodeRequest.abort();
      activeStoreCodeRequest = null;
    }
    resolvingStoreCode.value = false;
  }

  function storeCodeErrorMessage (error) {
    const responseBody = error && error.responseBody;
    const responseData = error && error.response && error.response.data;
    const messages = [
      responseBody && responseBody.message,
      responseData && responseData.message,
      error && error.message
    ];
    return String(messages.find(message => String(message || '').trim()) || '').trim();
  }

  function openDirectStoreDetail (store, returnState) {
    clearSuccessfulSearch();
    frequentStores.value = saveFrequentStore(store);
    saveSelectedStore(store);

    const restored = Object.assign({}, returnState, {
      frequentStores: frequentStores.value
    });
    applyListState(restored);
    syncSearchInput();
    saveCurrentListState();
    markStoreListStateForRestore();
    return router.push({
      name: 'detail',
      params: {storeCode: getStoreKey(store)},
      query: {source: 'public'}
    });
  }

  async function resolveDirectStoreCode (directStoreCode, options = {}, returnState = buildCurrentListState()) {
    const storeCode = String(directStoreCode && directStoreCode.storeCode || '').trim().toUpperCase();
    const trigger = options.trigger === 'submit' ? 'submit' : 'input';
    if (!storeCode) return null;

    cancelStoreRequest();
    clearPlaceResults();
    cancelStoreCodeRequest();
    const requestSeq = ++storeCodeRequestSeq;
    const controller = new AbortController();
    activeStoreCodeRequest = controller;
    resolvingStoreCode.value = true;
    storeLoading.value = true;
    storeError.value = '';
    stores1.value = [];
    nearbyLoaded.value = false;
    locateError.value = false;
    retryLocationOnReturn.value = false;
    locationErrorMessage.value = '';

    try {
      const response = await axios.post(
        API_ENDPOINTS.resolveStoreCode,
        {storeCode},
        {signal: controller.signal}
      );
      if (disposed || activeStoreCodeRequest !== controller || requestSeq !== storeCodeRequestSeq) {
        return null;
      }

      const body = response && response.data ? response.data : {};
      const store = body.data && body.data.store;
      if (body.code !== 0 || !store) {
        const error = new Error(body.message || '门店不存在或暂不可用');
        error.responseBody = body;
        throw error;
      }
      return await openDirectStoreDetail(store, returnState);
    } catch (error) {
      if (
        isCanceledRequest(error) ||
        disposed ||
        activeStoreCodeRequest !== controller ||
        requestSeq !== storeCodeRequestSeq
      ) {
        return null;
      }
      const message = storeCodeErrorMessage(error) || '门店不存在或暂不可用';
      storeError.value = `未识别到门店编码：${message}`;
      if (trigger === 'submit') {
        alert(storeError.value);
      }
      saveCurrentListState();
      return null;
    } finally {
      if (activeStoreCodeRequest === controller && requestSeq === storeCodeRequestSeq) {
        activeStoreCodeRequest = null;
        resolvingStoreCode.value = false;
        storeLoading.value = false;
      }
    }
  }

  async function requestStores (endpoint, data, showStoreCity, options = {}) {
    cancelStoreRequest();
    const controller = new AbortController();
    activeStoreRequest = controller;
    storeLoading.value = true;
    storeError.value = '';
    stores1.value = [];
    if (options.nearby === true) nearbyLoaded.value = false;
    try {
      const response = await axios.post(endpoint, data, {signal: controller.signal});
      if (!disposed && activeStoreRequest === controller) {
        setStoresFromResponse(response, showStoreCity, options);
        return response;
      }
      return null;
    } catch (error) {
      if (!isCanceledRequest(error) && !disposed && activeStoreRequest === controller) {
        console.error('Error fetching store info:', error);
        stores1.value = [];
        storeError.value = options.nearby === true
          ? '附近餐厅加载失败，请稍后重试'
          : '餐厅搜索暂不可用';
        nearbyLoaded.value = false;
        saveCurrentListState();
      }
      return null;
    } finally {
      if (activeStoreRequest === controller) {
        activeStoreRequest = null;
        storeLoading.value = false;
      }
    }
  }

  function searchByLbs (center = location.value, callScene = 'storeScene') {
    const deviceLocation = hasCoordinates(locatedCity.value) ? locatedCity.value : null;
    const requestData = {
      mylat: center.latitude,
      mylng: center.longitude,
      ...(center.coordinateSystem ? {coordinateSystem: center.coordinateSystem} : {}),
      ...(deviceLocation
        ? {
            phoneLatitude: deviceLocation.latitude,
            phoneLongitude: deviceLocation.longitude,
            phoneCoordinateSystem: deviceLocation.coordinateSystem || 'wgs84',
            phoneAccuracyMeters: deviceLocation.accuracyMeters ?? null,
            phoneTimestampMs: deviceLocation.timestampMs ?? null
          }
        : {}),
      callScene
    };
    return requestStores(API_ENDPOINTS.searchByLbs, requestData, false, {nearby: true});
  }

  function cancelPlaceRequest () {
    if (activePlaceRequest) {
      activePlaceRequest.abort();
      activePlaceRequest = null;
    }
  }

  function clearPlaceResults () {
    cancelPlaceRequest();
    places.value = [];
    placeLoading.value = false;
    placeError.value = '';
  }

  function getSearchCityName () {
    return String(location.value.cityName || location.value.name || '').trim();
  }

  function hasSearchCity () {
    const cityName = getSearchCityName();
    const cityCode = location.value.gbCityCode || location.value.id;
    return !!cityName &&
      cityName !== '定位失败' &&
      cityName !== '请选择' &&
      !!String(cityCode || '').trim();
  }

  function ensureCanSearchByCity () {
    if (hasSearchCity()) return true;

    cancelStoreRequest();
    clearPlaceResults();
    stores1.value = [];
    nearbyLoaded.value = false;
    storeLoading.value = false;
    storeError.value = '';
    locateError.value = true;
    retryLocationOnReturn.value = false;
    locationErrorMessage.value = '请先选择城市，或允许定位后再搜索门店';
    saveCurrentListState();
    return false;
  }

  async function requestPlaces () {
    cancelPlaceRequest();
    const keyword = keyWord.value.trim();
    const city = getSearchCityName();
    if (!keyword || !city || city === '定位失败' || city === '请选择') {
      places.value = [];
      placeError.value = city ? '' : '请先选择城市';
      return;
    }

    const controller = new AbortController();
    activePlaceRequest = controller;
    placeLoading.value = true;
    placeError.value = '';
    places.value = [];
    try {
      const response = await axios.get(API_ENDPOINTS.searchPlaces, {
        params: {keyword, city},
        signal: controller.signal
      });
      if (!disposed && activePlaceRequest === controller) {
        places.value = normalizePlaces(response);
        saveCurrentListState();
        return response;
      }
      return null;
    } catch (error) {
      if (!isCanceledRequest(error) && !disposed && activePlaceRequest === controller) {
        console.error('Error fetching place info:', error);
        places.value = [];
        placeError.value = '位置搜索暂不可用';
        saveCurrentListState();
      }
      return null;
    } finally {
      if (activePlaceRequest === controller) {
        activePlaceRequest = null;
        placeLoading.value = false;
      }
    }
  }

  async function searchKeywordResults (options = {}) {
    if (!ensureCanSearchByCity()) {
      clearSuccessfulSearch();
      return [];
    }

    const historyKeyword = keyWord.value.trim();
    const results = await Promise.allSettled([
      searchByCityCodeAndKeyword(),
      requestPlaces()
    ]);

    if (
      disposed ||
      resultMode.value !== 'keyword' ||
      keyWord.value.trim() !== historyKeyword
    ) {
      return results;
    }

    if (stores1.value.length > 0 || places.value.length > 0) {
      markSuccessfulSearch(historyKeyword);
      if (options.trigger === 'submit') {
        commitSuccessfulSearchKeyword();
      }
    } else {
      clearSuccessfulSearch();
    }
    return results;
  }

  function searchText (text, meta = {}) {
    const returnState = buildCurrentListState();
    const trigger = meta && meta.trigger === 'submit' ? 'submit' : 'input';
    const normalizedText = String(text || '').trim();

    if (normalizedText.startsWith('#') || normalizedText.startsWith('＃')) {
      cancelStoreRequest();
      cancelStoreCodeRequest();
      clearPlaceResults();
      clearSuccessfulSearch();
      pendingRecentKeywordAfterCity.value = '';
      txtLength.value = normalizedText.length;
      keyWord.value = normalizedText;
      searchMode.value = 'store';
      resultMode.value = 'store';
      selectedPlace.value = null;
      stores1.value = [];
      nearbyLoaded.value = false;
      locateError.value = false;
      storeError.value = '请直接输入门店编码，无需添加 #';
      if (trigger === 'submit') {
        alert(storeError.value);
      }
      saveCurrentListState();
      return false;
    }

    const directStoreCode = parseDirectStoreCodeText(normalizedText);
    if (directStoreCode) {
      clearSuccessfulSearch();
      pendingRecentKeywordAfterCity.value = '';
      txtLength.value = normalizedText.length;
      keyWord.value = directStoreCode.storeCode;
      searchMode.value = 'store';
      resultMode.value = 'store';
      selectedPlace.value = null;
      return resolveDirectStoreCode(directStoreCode, {trigger}, returnState);
    }

    cancelStoreCodeRequest();
    clearSuccessfulSearch();
    pendingRecentKeywordAfterCity.value = '';
    txtLength.value = normalizedText.length;
    keyWord.value = normalizedText;
    selectedPlace.value = null;

    if (!normalizedText.length) {
      searchMode.value = 'keyword';
      resultMode.value = 'nearby';
      isShowStoreCity.value = false;
      clearPlaceResults();
      return refreshStores();
    }

    searchMode.value = 'keyword';
    resultMode.value = 'keyword';
    locateError.value = false;
    locationErrorMessage.value = '';
    return searchKeywordResults({trigger});
  }

  function searchByStoreCodeAndName () {
    nearbyLoaded.value = false;
    return requestStores(API_ENDPOINTS.searchByStoreCodeAndName, {keyword: keyWord.value}, true);
  }

  function searchByCityCodeAndKeyword () {
    nearbyLoaded.value = false;
    const requestData = {
      keyword: keyWord.value,
      cityNameMy: location.value.cityName || location.value.name
    };
    if (location.value.districtCode && location.value.districtName) {
      requestData.districtNameMy = location.value.districtName;
      requestData.districtCode = location.value.districtCode;
    } else {
      requestData.gbCityCode = location.value.gbCityCode || String(location.value.id).substring(0, 6);
    }
    return requestStores(API_ENDPOINTS.searchByCityCodeAndKeyword, requestData, false);
  }

  function selectPlace (place) {
    if (!hasCoordinates(place)) return Promise.resolve();
    commitSuccessfulSearchKeyword();
    cancelPlaceRequest();
    places.value = [];
    placeLoading.value = false;
    placeError.value = '';
    selectedPlace.value = Object.assign({}, place, {
      latitude: Number(place.latitude),
      longitude: Number(place.longitude),
      coordinateSystem: place.coordinateSystem || 'gcj02'
    });
    keyWord.value = '';
    txtLength.value = 0;
    searchMode.value = 'keyword';
    resultMode.value = 'poi';
    isShowStoreCity.value = false;
    pendingSearchText.value = '';
    syncSearchInput();
    return searchByLbs(selectedPlace.value, 'storePoiScene');
  }

  function openStoreDetail (store) {
    commitSuccessfulSearchKeyword();
    frequentStores.value = saveFrequentStore(store);
    saveSelectedStore(store);
    saveCurrentListState();
    markStoreListStateForRestore();
    router.push({
      name: 'detail',
      params: {storeCode: getStoreKey(store)},
      query: {source: 'public'}
    });
  }

  function getRecentStoreKey (store) {
    return getStoreKey(store);
  }

  function recentStoreMeta (store) {
    const cityName = String((store && store.cityName) || '').trim();
    const storeCode = String((store && store.storecode) || '').trim();
    return [cityName, storeCode].filter(Boolean).join(' · ');
  }

  return {
    applyRecentKeyword, browsePrimaryActionText, browseSecondaryActionText,
    browseSectionTitle, browseStatus, browseStatusIsError, browseStatusMessage,
    browseStatusTitle, clearSearch, disInput1, emptyText, getCity, getRecentStoreKey,
    handleBrowsePrimaryAction, handleCityLocationError,
    handleScrollClick, isKeywordResults, isShowStoreCity, keyWord, locateError,
    locatedCity, locating, locationErrorMessage, nearbyLoaded, nearbyRadiusKilometers, nearbyTitle,
    retryCurrentLocation, openStoreDetail, placeError, placeLoading, places,
    recentQuickStores, recentStoreKeywords, recentStoreMeta, resolvingStoreCode, scrollData,
    searchKey, searchStoreRef, searchText, selectPlace, setScrollDistance, showCity,
    showBrowseSection, showEmpty, showNearbyLimit, showRecentKeywordBar,
    showRecentQuickStores, showToast, storeError, storeLoading, suggest11, visibleStores,
    vuecity
  };
}
